import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Link } from 'wouter';
import { useTRPC } from '../trpc.js';
import { useToast } from '../ui/Toast.js';
import { setAddTarget, useAddTarget } from './addTarget.js';

/**
 * The funds table's half of "Pick from the funds table": while a list is the
 * target, the selected rows go onto it from here, and the way back to it is
 * one click. Renders nothing when no list is the target.
 */
export function AddTargetBar(props: { fundIds: readonly string[] }): React.JSX.Element | null {
  const targetId = useAddTarget();
  const trpc = useTRPC();
  const toast = useToast();
  const queryClient = useQueryClient();
  const lists = useQuery(trpc.lists.list.queryOptions(undefined, { enabled: targetId !== null }));
  const add = useMutation(
    trpc.lists.addItems.mutationOptions({
      onSuccess: (answer) => {
        const name = answer.list?.name ?? 'the list';
        const added = answer.added;
        const there = answer.alreadyThere;
        toast(`${added} added to ${name}${there > 0 ? `, ${there} already there` : ''}.`, {
          tone: 'info',
        });
        if (targetId !== null) {
          void queryClient.invalidateQueries({
            queryKey: trpc.lists.get.queryKey({ id: targetId }),
          });
        }
        void queryClient.invalidateQueries({ queryKey: trpc.lists.list.queryKey() });
      },
      onError: (error) => toast(error.message),
    }),
  );

  const target = lists.data?.find((list) => list.id === targetId);
  // The list was deleted since it was chosen.
  const gone = targetId !== null && lists.data !== undefined && target === undefined;
  useEffect(() => {
    if (gone) setAddTarget(null);
  }, [gone]);

  if (targetId === null || target === undefined) return null;

  return (
    <>
      <button
        className="button primary"
        type="button"
        disabled={props.fundIds.length === 0 || add.isPending}
        onClick={() => add.mutate({ id: targetId, fundIds: [...props.fundIds] })}
      >
        Add selected to {target.name}
      </button>
      <Link className="link-button" href={`/lists/${targetId}`}>
        Back to list
      </Link>
      <button className="text-button" type="button" onClick={() => setAddTarget(null)}>
        Clear
      </button>
    </>
  );
}
